import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APIKey, apiRoutes } from 'constants/apiKeys';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import baseFetcher from 'shared/helpers/baseFetcher';

export interface Industry {
  id: string;
  name: string;
}

interface CompanySizeChoice {
  code: string;
  display: string;
}

interface CompanySizeResponse {
  company_sizes: CompanySizeChoice[];
}

export function useIndustries(language: string) {
  return useQuery<Industry[]>({
    queryKey: [APIKey.DEMO_INDUSTRIES, language],
    queryFn: async () => {
      const data: Record<string, string> = await baseFetcher(
        apiRoutes.demoIndustries(),
        { headers: { 'Accept-Language': language } }
      );
      return Object.entries(data).map(([id, name]) => ({ id, name }));
    },
  });
}

export function useCompanySizeChoices(language: string) {
  return useQuery<CompanySizeChoice[]>({
    queryKey: [APIKey.DEMO_COMPANY_SIZE_CHOICES, language],
    queryFn: async () => {
      const data: CompanySizeResponse = await baseFetcher(
        apiRoutes.demoCompanySizeChoices(),
        { headers: { 'Accept-Language': language } }
      );
      return data.company_sizes;
    },
  });
}

export class RegistrationError extends Error {
  status: number;

  fieldErrors: Record<string, unknown>;

  constructor(status: number, fieldErrors: Record<string, unknown>) {
    super('Registration failed');
    this.name = 'RegistrationError';
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

export interface RegisterValues {
  first_name: string;
  last_name: string;
  email: string;
  language: string;
  industry: string;
  additional_infos: {
    user_specific_industry: string;
    job_title: string;
    experience_in_years: string;
    company_size: string;
    team_size: string;
    issues: string;
  };
  recaptcha_v3_token?: string;
}

export function useRegisterMutation() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  return useMutation<unknown, RegistrationError, RegisterValues>({
    mutationFn: async (values) => {
      let finalValues = values;

      if (import.meta.env.REACT_APP_DEBUG !== 'true') {
        if (!executeRecaptcha) {
          throw new Error('reCAPTCHA not available');
        }
        const token = await executeRecaptcha('signup');
        finalValues = { ...values, recaptcha_v3_token: token };
      }
      try {
        const data = await baseFetcher(apiRoutes.demoRegister(), {
          method: 'POST',
          data: finalValues,
        });
        return data;
      } catch (error) {
        throw new RegistrationError(error.status, error.data);
      }
    },
  });
}

interface DemoApiResponse {
  status: string;
}

interface ResetDemoData {
  user: unknown;
}

export function useResetDemoMutation() {
  return useMutation<DemoApiResponse, Error, ResetDemoData>({
    mutationFn: (data) =>
      baseFetcher(apiRoutes.demoReset(), { method: 'PUT', data }),
  });
}

interface UpdateDemoIndustryData {
  industry: string;
  user_id: string;
}

export function useUpdateDemoIndustryMutation() {
  const queryClient = useQueryClient();
  return useMutation<DemoApiResponse, Error, UpdateDemoIndustryData>({
    mutationFn: (data) =>
      baseFetcher(apiRoutes.demoReset(), { method: 'PUT', data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APIKey.ECOACH_SESSIONS] });
    },
  });
}
